import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  selectAuth,
  setPrivileges,
  setToken,
} from "../../../../redux/features/auth/auth-slice";
import { resetBillingSpaceFilter } from "../../../../redux/features/billing-space-filter/billing-space-filter-slice";
import {
  selectIsOnCancelPressed,
  selectIsOnConfirmPressed,
  setIsOnCancelPressed,
  setIsOnConfirmPressed,
  setIsShowEstimationInterruptModal,
} from "../../../../redux/features/solar-estimation/solarEstimationSlice";
import {
  selectTheme,
  setTheme,
} from "../../../../redux/features/theme/theme-slice";
import {
  resetProfile,
  selectProfile,
} from "../../../../redux/features/user/user-slice";
import { UserRole } from "../../enums/user-roles";
import { AppRoute } from "../../interfaces/app-routes";
import IPageInfo from "../../interfaces/page-info";
import FeedbackModal from "../../shared-components/feedback-modal/feedback-modal";
import LogoutModal from "../../shared-components/logout-modal/logout-modal";
import MenuItem from "../../shared-components/sidebar/components/menu-item/menu-item";
import { isOnEstimation } from "../../utils/solarEstimationHelper";

interface IProps {
  collapsed?: boolean;
  closeMobileNav?: () => void;
  routes: IPageInfo[];
  logos: string[];
}

const AppMenuContent = (props: IProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { collapsed = false, closeMobileNav, routes, logos } = props;
  const [showLogout, setShowLogout] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const isOnConfirmPressed = useSelector(selectIsOnConfirmPressed);
  const isOnCancelPressed = useSelector(selectIsOnCancelPressed);
  const theme = useSelector(selectTheme);
  const [navigateOnInterruption, setNavigateOnInterruption] = useState("");

  const profile = useSelector(selectProfile);
  const auth = useSelector(selectAuth);

  useEffect(() => {
    if (isOnConfirmPressed && navigateOnInterruption) {
      dispatch(setIsShowEstimationInterruptModal(false));
      dispatch(setIsOnConfirmPressed(false));
      setNavigateOnInterruption("");

      setShowLogout(true);
    }
  }, [isOnConfirmPressed, navigateOnInterruption]);

  useEffect(() => {
    if (isOnCancelPressed && navigateOnInterruption) {
      setNavigateOnInterruption("");
      dispatch(setIsOnCancelPressed(false));
    }
  }, [isOnCancelPressed, navigateOnInterruption]);

  const logout = () => {
    const country = localStorage.getItem("country");
    sessionStorage.clear();
    localStorage.clear();
    dispatch(setToken(""));
    dispatch(resetProfile());
    dispatch(setPrivileges([]));
    dispatch(resetBillingSpaceFilter());
    dispatch(setTheme(theme));

    if (country !== null) {
      localStorage.setItem("country", country);
    }

    if (routes) {
      if (
        profile?.role === UserRole.ROLE_ADMIN ||
        profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
      ) {
        navigate(AppRoute.ADMIN_LOGIN);
      } else {
        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM);
      }
    }
  };

  return (
    <Container className="h-100" style={{ zIndex: 9999 }}>
      <div className="h-100 d-flex flex-column justify-content-between py-4 bg-side-bar">
        <Row>
          <Col>
            <Row>
              <Col className="text-center d-flex justify-content-center">
                {collapsed ? (
                  <a
                    href="https://chlorideexide.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src={logos[1]}
                      alt="Chloride Exide Logo"
                      className="d-block"
                      width={60}
                      height={60}
                    />
                  </a>
                ) : (
                  <a
                    href="https://chlorideexide.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "inline-block" }}
                  >
                    <img
                      src={logos[0]}
                      alt="Chloride Exide Logo"
                      className="d-block mx-auto"
                      width={80}
                      height={80}
                    />
                  </a>
                )}
              </Col>
            </Row>
            <Row className="mt-4">
              {routes.map((sc) => {
                return sc?.subRoutes
                  ?.filter(
                    (item) =>
                      item.showOnDrawer &&
                      (item.privilege
                        ? auth.privileges.includes(item.privilege)
                        : true),
                  )
                  .map((subRoute) => (
                    <Col
                      xs={12}
                      key={subRoute.path}
                      className="py-3 text-center d-flex justify-content-center"
                    >
                      <MenuItem
                        active={pathname.includes(subRoute.path)}
                        collapsed={props.collapsed}
                        icon={subRoute.icon}
                        to={subRoute.path}
                        text={subRoute.name}
                        onClick={closeMobileNav && closeMobileNav}
                        enableRoute={subRoute.enableRoute}
                        bgColor={subRoute.bgColor}
                        bgColorIcon={subRoute.bgColorIcon}
                      />
                    </Col>
                  ));
              })}
            </Row>
          </Col>
        </Row>
        <Row className="justify-content-center py-3">
          <Col xs="auto">
            {/* {profile?.role === UserRole.ROLE_USER && (
              <FeedbackButton
                collapsed={props.collapsed}
                setShowFeedback={() => setShowFeedback(true)}
              />
            )} */}
            {auth.token && (
              <Row className="mt-1">
                <Col>
                  <MenuItem
                    active={false}
                    collapsed={props.collapsed}
                    icon="logout"
                    onClick={() => {
                      if (isOnEstimation(pathname)) {
                        dispatch(setIsShowEstimationInterruptModal(true));
                        setNavigateOnInterruption("logout");
                        return;
                      }

                      setShowLogout(true);
                    }}
                    text="Logout"
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </div>
      <LogoutModal
        show={showLogout}
        onCancel={() => setShowLogout(false)}
        onClose={() => setShowLogout(false)}
        onConfirm={() => {
          setShowLogout(false);
          logout();
        }}
      />
      <FeedbackModal
        show={showFeedback}
        onCancel={() => setShowFeedback(false)}
        onClose={() => setShowFeedback(false)}
        onConfirm={() => {
          setShowFeedback(false);
        }}
      />
    </Container>
  );
};

export default AppMenuContent;
